// modules/BattlePlayer.js
import VehicleEquipmentSystem from './VehicleEquipmentSystem.js';
import DebugHelper from './DebugHelper.js';
import { getVehicleTextureKey } from './VehicleTextureHelper.js';

export default class BattlePlayer {
    constructor(scene) {
        this.scene = scene;
        this.player = null;
        this.playerSprite = null;
        this.equipmentAttackBonus = 0;
        this.equipmentDefenseBonus = 0;
        
        // 组队模式：存储所有队员的显示对象
        this.teamMemberSprites = []; // 存储所有队员的精灵数组
        this.teamMemberUIs = []; // 存储所有队员的UI数组
        this.currentMemberIndex = -1; // 当前操作的队员索引
    }

    init(playerData) {
        // 使用引用而不是复制，这样装备变化会自动反映
        // 但需要确保equipment数组是独立的，避免直接修改原对象
        this.player = playerData;
        // 确保equipment数组存在
        if (!this.player.equipment) {
            this.player.equipment = [];
        }
        this.calculateEquipmentBonuses();
        
        // 初始化战车SP最大值（基于载重）
        if (this.player.vehicle) {
            const maxSp = VehicleEquipmentSystem.calculateMaxSp(this.player.vehicle);
            this.player.vehicle.maxSp = maxSp;
            // 如果当前SP超过最大值，则设为最大值
            if (this.player.vehicle.sp > maxSp) {
                this.player.vehicle.sp = maxSp;
            }
        }
    }

    create() {
        // 检查是否是组队模式
        const isTeamBattle = this.scene.isTeamBattle;
        const teamMembers = this.scene.teamMembers || [];
        
        if (isTeamBattle && teamMembers.length > 0) {
            // 组队模式：创建所有队员的显示
            return this.createTeamMembers();
        } else {
            // 单人模式：创建单个玩家
            return this.createSinglePlayer();
        }
    }
    
    /**
     * 创建单个玩家（单人模式）
     */
    createSinglePlayer() {
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        const playerX = screenWidth * 0.75;
        const playerY = screenHeight * 0.5;

        // 根据是否在战车中选择显示
        let texture, scale;
        if (this.player.inVehicle && this.player.vehicle) {
            texture = getVehicleTextureKey(this.player.vehicle);
            scale = 1.5;
        } else {
            texture = this.player.battle;
            scale = 0.75;
        }

        // 创建玩家精灵
        this.playerSprite = this.scene.add.image(playerX, playerY, texture);
        this.playerSprite.setScale(scale);
        
        // 创建UI
        this.createPlayerUI();
        
        return this.playerSprite;
    }
    
    /**
     * 创建所有队伍成员（组队模式，最多3个，从上到下排列）
     */
    createTeamMembers() {
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        const baseX = screenWidth * 0.75;
        const baseY = screenHeight * 0.3; // 从上部开始
        const spacing = 120; // 每个队员之间的间距
        
        const teamMembers = this.scene.teamMembers || [];
        const maxMembers = Math.min(teamMembers.length, 3); // 最多显示3个
        
        DebugHelper.log('创建队伍成员显示:', {
            teamMembersCount: teamMembers.length,
            maxMembers: maxMembers,
            teamMembers: teamMembers.map(m => ({
                playerId: m.playerId,
                playerName: m.playerName,
                hasPlayerData: !!m.playerData
            }))
        });
        
        // 清除之前的显示
        this.clearTeamMembers();
        
        for (let i = 0; i < maxMembers; i++) {
            const member = teamMembers[i];
            const memberData = member.playerData || this.player; // 使用队员的数据，如果没有则使用当前玩家数据
            
            // 计算Y位置（从上到下排列）
            const playerY = baseY + (i * spacing);
            
            // 根据是否在战车中选择显示
            let texture, scale;
            if (memberData.inVehicle && memberData.vehicle) {
                texture = getVehicleTextureKey(memberData.vehicle);
                scale = 1.5;
            } else {
                texture = memberData.battle;
                scale = 0.75;
            }
            
            // 创建队员精灵
            const sprite = this.scene.add.image(baseX, playerY, texture);
            sprite.setScale(scale);
            
            // 创建队员UI
            const ui = this.createMemberUI(sprite, memberData, member.playerName || memberData.name || '玩家', i);
            
            // 保存到数组
            this.teamMemberSprites.push(sprite);
            this.teamMemberUIs.push(ui);
        }
        
        // 设置当前操作的队员（默认第一个）
        if (this.teamMemberSprites.length > 0) {
            this.playerSprite = this.teamMemberSprites[0]; // 保持向后兼容
            this.currentMemberIndex = 0;
            // 初始高亮第一个队员
            this.switchToMember(0);
        }
        
        return this.playerSprite;
    }
    
    /**
     * 创建单个队员的UI
     */
    createMemberUI(sprite, memberData, memberName, index) {
        const x = sprite.x;
        const y = sprite.y;
        
        const ui = {
            sprite: sprite,
            hpBarBg: null,
            hpBarBorder: null,
            hpBar: null,
            nameText: null,
            statusEffectsContainer: null,
            statusEffectIcons: []
        };
        
        // HP条
        const barWidth = 80;
        const barHeight = 8;
        
        // HP条背景
        ui.hpBarBg = this.scene.add.graphics();
        ui.hpBarBg.fillStyle(0x000000, 0.7);
        ui.hpBarBg.fillRect(x - barWidth / 2, y - 55, barWidth, barHeight);
        
        // HP条边框
        ui.hpBarBorder = this.scene.add.graphics();
        ui.hpBarBorder.lineStyle(1, 0xffffff, 0.5);
        ui.hpBarBorder.strokeRect(x - barWidth / 2 - 1, y - 55 - 1, barWidth + 2, barHeight + 2);
        
        // HP条
        ui.hpBar = this.scene.add.graphics();
        this.updateMemberHPBar(ui, memberData);
        
        // 队员名称
        ui.nameText = this.scene.add.text(
            x,
            y - 40,
            memberName,
            {
                fontSize: '14px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#006400',
                padding: { left: 5, right: 5, top: 2, bottom: 2 }
            }
        ).setOrigin(0.5, 0.5);
        
        // 状态效果容器
        ui.statusEffectsContainer = this.scene.add.container(x, y - 70);
        ui.statusEffectIcons = [];
        
        return ui;
    }
    
    /**
     * 更新单个队员的HP条
     */
    updateMemberHPBar(ui, memberData) {
        if (!ui || !ui.hpBar || !memberData) return;
        
        ui.hpBar.clear();
        
        const x = ui.sprite.x;
        const y = ui.sprite.y;
        const barWidth = 80;
        const barHeight = 8;
        
        // 根据是否在战车中显示SP或HP
        let current, max, percent;
        if (memberData.inVehicle && memberData.vehicle) {
            const maxSp = VehicleEquipmentSystem.calculateMaxSp(memberData.vehicle);
            current = memberData.vehicle.sp || 0;
            max = maxSp;
            percent = max > 0 ? Math.max(0, current / max) : 0;
        } else {
            current = memberData.hp || 0;
            max = memberData.maxHp || 1;
            percent = Math.max(0, current / max);
        }
        
        // 根据百分比选择颜色
        let color;
        if (percent > 0.6) {
            color = 0x00ff00; // 绿色
        } else if (percent > 0.3) {
            color = 0xffff00; // 黄色
        } else {
            color = 0xff0000; // 红色
        }
        
        ui.hpBar.fillStyle(color, 1);
        ui.hpBar.fillRect(x - barWidth / 2, y - 55, barWidth * percent, barHeight);
    }
    
    /**
     * 清除所有队员的显示
     */
    clearTeamMembers() {
        // 清除之前的队员显示
        this.teamMemberSprites.forEach(sprite => {
            if (sprite && sprite.destroy) sprite.destroy();
        });
        this.teamMemberUIs.forEach(ui => {
            if (ui.hpBarBg && ui.hpBarBg.destroy) ui.hpBarBg.destroy();
            if (ui.hpBarBorder && ui.hpBarBorder.destroy) ui.hpBarBorder.destroy();
            if (ui.hpBar && ui.hpBar.destroy) ui.hpBar.destroy();
            if (ui.nameText && ui.nameText.destroy) ui.nameText.destroy();
            if (ui.statusEffectsContainer && ui.statusEffectsContainer.destroy) ui.statusEffectsContainer.destroy();
        });
        this.teamMemberSprites = [];
        this.teamMemberUIs = [];
    }
    
    /**
     * 切换当前操作的队员（组队模式）
     */
    switchToMember(memberIndex) {
        if (!this.scene.isTeamBattle || memberIndex < 0 || memberIndex >= this.teamMemberSprites.length) {
            return;
        }
        
        // 更新当前队员索引
        this.currentMemberIndex = memberIndex;
        
        // 高亮当前操作的队员（其他队员降低透明度）
        this.teamMemberSprites.forEach((sprite, index) => {
            if (index === memberIndex) {
                sprite.setAlpha(1.0); // 当前队员完全不透明
                // 获取原始缩放值（根据是否在战车中）
                const teamMembers = this.scene.teamMembers || [];
                const memberData = teamMembers[index]?.playerData;
                const baseScale = memberData?.inVehicle && memberData?.vehicle ? 1.5 : 0.75;
                sprite.setScale(baseScale * 1.1); // 稍微放大
            } else {
                sprite.setAlpha(0.6); // 其他队员半透明
                // 恢复原始大小
                const teamMembers = this.scene.teamMembers || [];
                const memberData = teamMembers[index]?.playerData;
                const baseScale = memberData?.inVehicle && memberData?.vehicle ? 1.5 : 0.75;
                sprite.setScale(baseScale);
            }
        });
        
        // 更新当前玩家数据
        const teamMembers = this.scene.teamMembers || [];
        if (teamMembers[memberIndex]) {
            const memberData = teamMembers[memberIndex].playerData;
            if (memberData) {
                this.init(memberData); // 更新当前玩家数据
                this.playerSprite = this.teamMemberSprites[memberIndex]; // 更新当前精灵引用
            }
        }
    }

    createPlayerUI() {
        const x = this.playerSprite.x;
        const y = this.playerSprite.y;
        
        // HP条
        const barWidth = 80;
        const barHeight = 8;
        
        // HP条背景
        this.hpBarBg = this.scene.add.graphics();
        this.hpBarBg.fillStyle(0x000000, 0.7);
        this.hpBarBg.fillRect(x - barWidth / 2, y - 55, barWidth, barHeight);
        
        // HP条边框
        this.hpBarBorder = this.scene.add.graphics();
        this.hpBarBorder.lineStyle(1, 0xffffff, 0.5);
        this.hpBarBorder.strokeRect(x - barWidth / 2 - 1, y - 55 - 1, barWidth + 2, barHeight + 2);
        
        // HP条
        this.hpBar = this.scene.add.graphics();
        this.updatePlayerHPBar();
        
        // 玩家名称
        this.nameText = this.scene.add.text(
            x,
            y - 40,
            this.player.name,
            {
                fontSize: '14px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#006400',
                padding: { left: 5, right: 5, top: 2, bottom: 2 }
            }
        ).setOrigin(0.5, 0.5);
        
        // 状态效果容器
        this.statusEffectsContainer = this.scene.add.container(x, y - 70);
        this.statusEffectIcons = [];
    }
    
    /**
     * 更新玩家精灵外观（上/下车时调用）
     */
    updatePlayerAppearance() {
        if (!this.playerSprite || !this.player) return;
        
        // 根据是否在战车中选择显示
        let texture, scale;
        if (this.player.inVehicle && this.player.vehicle) {
            texture = getVehicleTextureKey(this.player.vehicle);
            scale = 1.5;
            // 更新战车SP最大值（基于载重）
            const maxSp = VehicleEquipmentSystem.calculateMaxSp(this.player.vehicle);
            this.player.vehicle.maxSp = maxSp;
            // 如果当前SP超过最大值，则设为最大值
            if (this.player.vehicle.sp > maxSp) {
                this.player.vehicle.sp = maxSp;
            }
        } else {
            texture = this.player.battle;
            scale = 0.75;
        }
        
        // 检查纹理是否存在
        if (!this.scene.textures.exists(texture)) {
            DebugHelper.warn(`BattlePlayer: 纹理 ${texture} 不存在，使用默认纹理`);
            return;
        }
        
        // 更新精灵纹理和缩放
        this.playerSprite.setTexture(texture);
        if (this.player.inVehicle && this.player.vehicle && this.playerSprite.setFrame) {
            this.playerSprite.setFrame(0);
        }
        this.playerSprite.setScale(scale);
        
        // 更新HP/SP条显示
        this.updatePlayerHPBar();
    }
    
    updateStatusEffects() {
        // 组队模式：只更新当前操作的队员的状态效果
        if (this.scene.isTeamBattle) {
            if (this.currentMemberIndex >= 0 && this.currentMemberIndex < this.teamMemberUIs.length) {
                const ui = this.teamMemberUIs[this.currentMemberIndex];
                if (ui && ui.statusEffectsContainer && this.player) {
                    // 更新当前队员的状态效果（使用当前队员的容器）
                    this.updateStatusEffectsForMember(ui, this.player);
                }
            }
            return;
        }
        
        // 单人模式：更新当前玩家的状态效果
        if (!this.statusEffectsContainer || !this.player) return;
        
        this.updateStatusEffectsForMember(
            {
                statusEffectsContainer: this.statusEffectsContainer,
                statusEffectIcons: this.statusEffectIcons
            },
            this.player
        );
    }
    
    /**
     * 更新单个队员的状态效果
     */
    updateStatusEffectsForMember(ui, playerData) {
        if (!ui || !ui.statusEffectsContainer || !playerData) return;
        
        // 清除旧的状态图标
        if (ui.statusEffectIcons) {
            ui.statusEffectIcons.forEach(icon => {
                if (icon && icon.destroy) icon.destroy();
            });
            ui.statusEffectIcons = [];
        } else {
            ui.statusEffectIcons = [];
        }
        
        const statuses = [];
        let offsetX = -40;
        
        // 燃烧状态
        if (playerData.burning && playerData.burningTurns > 0) {
            statuses.push({
                type: 'burning',
                turns: playerData.burningTurns,
                color: 0xff6600,
                symbol: '🔥'
            });
        }
        
        // 中毒状态
        if (playerData.poisoned && playerData.poisonTurns > 0) {
            statuses.push({
                type: 'poisoned',
                turns: playerData.poisonTurns,
                color: 0x00ff00,
                symbol: '☠'
            });
        }
        
        // 冰冻状态
        if (playerData.frozen && playerData.frozenTurns > 0) {
            statuses.push({
                type: 'frozen',
                turns: playerData.frozenTurns,
                color: 0x00ffff,
                symbol: '❄'
            });
        }
        
        // 创建状态图标
        statuses.forEach((status, index) => {
            // 状态图标背景
            const iconBg = this.scene.add.graphics();
            iconBg.fillStyle(status.color, 0.8);
            iconBg.fillCircle(offsetX + index * 30, 0, 12);
            iconBg.lineStyle(2, 0xffffff, 1);
            iconBg.strokeCircle(offsetX + index * 30, 0, 12);
            
            // 状态图标文字
            const iconText = this.scene.add.text(
                offsetX + index * 30,
                0,
                status.symbol,
                {
                    fontSize: '16px',
                    fill: '#ffffff',
                    fontFamily: 'monospace'
                }
            ).setOrigin(0.5);
            
            // 剩余回合数
            const turnsText = this.scene.add.text(
                offsetX + index * 30,
                15,
                status.turns.toString(),
                {
                    fontSize: '10px',
                    fill: '#ffffff',
                    fontFamily: 'monospace',
                    backgroundColor: '#000000',
                    padding: { left: 3, right: 3, top: 1, bottom: 1 }
                }
            ).setOrigin(0.5);
            
            ui.statusEffectsContainer.add([iconBg, iconText, turnsText]);
            ui.statusEffectIcons.push({ bg: iconBg, text: iconText, turns: turnsText });
        });
    }

    updatePlayerHPBar() {
        // 如果是组队模式，更新所有队员的HP条
        if (this.scene.isTeamBattle && this.teamMemberUIs.length > 0) {
            const teamMembers = this.scene.teamMembers || [];
            this.teamMemberUIs.forEach((ui, index) => {
                if (teamMembers[index] && teamMembers[index].playerData) {
                    this.updateMemberHPBar(ui, teamMembers[index].playerData);
                }
            });
            // 更新当前操作的队员的状态效果
            if (this.currentMemberIndex >= 0 && this.currentMemberIndex < this.teamMemberUIs.length) {
                this.updateStatusEffects();
            }
            return;
        }
        
        // 单人模式：更新当前玩家的HP条
        if (!this.hpBar || !this.player) return;
        
        this.hpBar.clear();
        
        const x = this.playerSprite.x;
        const y = this.playerSprite.y;
        const barWidth = 80;
        const barHeight = 8;
        
        // 根据是否在战车中显示SP或HP
        let current, max, percent;
        if (this.player.inVehicle && this.player.vehicle) {
            // 在战车中：显示SP
            const maxSp = VehicleEquipmentSystem.calculateMaxSp(this.player.vehicle);
            this.player.vehicle.maxSp = maxSp; // 确保最大值正确
            current = this.player.vehicle.sp || 0;
            max = maxSp;
            percent = max > 0 ? Math.max(0, current / max) : 0;
        } else {
            // 不在战车中：显示HP
            current = this.player.hp || 0;
            max = this.player.maxHp || 1;
            percent = Math.max(0, current / max);
        }
        
        // 根据百分比选择颜色
        let color;
        if (percent > 0.6) {
            color = 0x00ff00; // 绿色
        } else if (percent > 0.3) {
            color = 0xffff00; // 黄色
        } else {
            color = 0xff0000; // 红色
        }
        
        this.hpBar.fillStyle(color, 1);
        this.hpBar.fillRect(x - barWidth / 2, y - 55, barWidth * percent, barHeight);
        
        // 更新状态效果显示
        this.updateStatusEffects();
    }

    calculateEquipmentBonuses() {
        this.equipmentAttackBonus = 0;
        this.equipmentDefenseBonus = 0;
        
        if (this.player && this.player.equipment) {
            this.player.equipment.forEach(item => {
                if (item) {
                    if (item.damage) this.equipmentAttackBonus += item.damage;
                    if (item.defense) this.equipmentDefenseBonus += item.defense;
                }
            });
        }
    }
    

    getCurrentWeapon() {
        if (!this.player || !this.player.equipment) return null;
        
        return this.player.equipment.find(item => 
            item && (item.slot === 'weapon' || item.type === 'weapon')
        );
    }

    applyDamage(damage) {
        if (!this.player) return false;
        
        let wasDefeated = false;
        let shouldExitVehicle = false;
        
        if (this.player.inVehicle && this.player.vehicle) {
            // 在战车中：扣除SP
            if (!this.player.vehicle.sp) {
                this.player.vehicle.sp = 0;
            }
            
            // 如果SP已经为0，强制下车
            if (this.player.vehicle.sp <= 0) {
                shouldExitVehicle = true;
            } else {
                // 扣除SP
                this.player.vehicle.sp -= damage;
                if (this.player.vehicle.sp < 0) this.player.vehicle.sp = 0;
                
                // SP为0后，再次受攻击时强制下车
                if (this.player.vehicle.sp <= 0) {
                    shouldExitVehicle = true;
                }
            }
        } else {
            // 不在战车中：扣除HP
            this.player.hp -= damage;
            if (this.player.hp < 0) {
                this.player.hp = 0;
                wasDefeated = true;
            }
        }
        
        // 受到攻击后清除防御状态
        if (this.player.defending) {
            this.player.defending = false;
            this.player.defenseBonus = null;
        }
        
        this.updatePlayerHPBar();
        
        // 受伤动画
        this.scene.tweens.add({
            targets: this.playerSprite,
            scale: { from: this.playerSprite.scale, to: this.playerSprite.scale * 1.1 },
            duration: 100,
            yoyo: true
        });
        
        // 返回是否应该强制下车
        return { wasDefeated, shouldExitVehicle };
    }

    heal(amount) {
        if (!this.player) return;
        
        if (this.player.inVehicle && this.player.vehicle) {
            // 在战车中：恢复SP
            const maxSp = VehicleEquipmentSystem.calculateMaxSp(this.player.vehicle);
            this.player.vehicle.maxSp = maxSp; // 确保最大值正确
            this.player.vehicle.sp = Math.min(maxSp, (this.player.vehicle.sp || 0) + amount);
        } else {
            // 不在战车中：恢复HP
            this.player.hp = Math.min(this.player.maxHp, this.player.hp + amount);
        }
        
        this.updatePlayerHPBar();
    }

    isDefeated() {
        if (!this.player) return true;
        
        if (this.player.inVehicle && this.player.vehicle) {
            // 在战车中：检查SP是否为0（SP为0时会被强制下车，不算击败）
            return false; // SP为0会强制下车，但不算击败
        } else {
            // 不在战车中：检查HP
            return this.player.hp <= 0;
        }
    }

    getAttackPower() {
        // 实时计算装备攻击加成，而不是使用缓存值
        let attackBonus = 0;
        if (this.player && this.player.equipment) {
            this.player.equipment.forEach(item => {
                if (item && item.damage) {
                    attackBonus += item.damage;
                }
            });
        }
        return (this.player.attack || 0) + attackBonus;
    }

    getDefensePower() {
        // 实时计算装备防御加成，而不是使用缓存值
        let defenseBonus = 0;
        if (this.player && this.player.equipment) {
            this.player.equipment.forEach(item => {
                if (item && item.defense) {
                    defenseBonus += item.defense;
                }
            });
        }
        return (this.player.defense || 0) + defenseBonus;
    }

    getPlayer() {
        return this.player;
    }

    levelUp(stats) {
        if (!this.player) return;
        
        this.player.level++;
        this.player.maxHp += stats.maxHp || 10;
        this.player.hp = this.player.maxHp;
        this.player.attack += stats.attack || 2;
        this.player.defense += stats.defense || 1;
        
        this.updatePlayerHPBar();
    }

    addExp(exp) {
        if (!this.player) return;
        
        this.player.exp += exp;
        
        // 检查升级
        while (this.player.exp >= this.player.nextLevelExp) {
            const expOverflow = this.player.exp - this.player.nextLevelExp;
            this.levelUp({
                maxHp: 10,
                attack: 2,
                defense: 1
            });
            this.player.exp = expOverflow;
            this.player.nextLevelExp = Math.floor(this.player.nextLevelExp * 1.5);
        }
    }

    addMoney(amount) {
        if (!this.player) return;
        this.player.money += amount;
    }

    destroy() {
        // 清理组队成员的显示
        this.clearTeamMembers();
        
        // 清理单人模式的显示
        if (this.hpBarBg) this.hpBarBg.destroy();
        if (this.hpBarBorder) this.hpBarBorder.destroy();
        if (this.hpBar) this.hpBar.destroy();
        if (this.nameText) this.nameText.destroy();
        if (this.statusEffectsContainer) this.statusEffectsContainer.destroy();
        this.statusEffectIcons.forEach(icon => {
            if (icon && icon.destroy) icon.destroy();
        });
        if (this.playerSprite && this.playerSprite.destroy) {
            this.playerSprite.destroy();
        }
    }
}